/** Client of an extranet API: same origin, cookie session, errors with a readable message. */

export interface ApiIssue {
  path: (string | number)[];
  message: string;
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly data?: { error?: string; message?: string; issues?: ApiIssue[]; [field: string]: unknown }) {
    super(message);
    this.name = "ApiError";
  }
}

/** A fixed message, or one built from the error body (undefined falls back to the API's own message). */
export type ErrorMessage = string | ((data: NonNullable<ApiError["data"]>) => string | undefined);

let onUnauthorized: (() => void) | undefined;
const errorMessages = new Map<string, ErrorMessage>();

/** The app registers here what to do when the session expires. */
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

/** Readable messages for an app's own error codes, e.g. { ari_source_conflict: "…" }. */
export function registerErrorMessages(messages: Record<string, ErrorMessage>) {
  for (const [code, text] of Object.entries(messages)) errorMessages.set(code, text);
}

function describe(status: number, data: ApiError["data"]): string {
  if (!data) return `Error ${status}`;
  const entry = data.error ? errorMessages.get(data.error) : undefined;
  const known = typeof entry === "function" ? entry(data) : entry;
  if (known) return known;
  if (data.error === "forbidden") return "You do not have permission to do this.";
  if (data.issues) return data.issues.map((i) => `${i.path.join(".") || "request"}: ${translate(i.message)}`).join("\n");
  return data.message ?? data.error ?? `Error ${status}`;
}

/** Validation messages shown to the user. The API already returns them in English. */
export function translate(message: string): string {
  return message;
}

export async function api<T = unknown>(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body !== undefined ? { "content-type": "application/json" } : {},
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const data = res.headers.get("content-type")?.includes("json") ? await res.json() : undefined;
  if (res.status === 401 && path !== "/auth/login" && path !== "/auth/password") onUnauthorized?.();
  if (!res.ok) throw new ApiError(res.status, describe(res.status, data), data);
  return data as T;
}

export const errorText = (err: unknown) => (err instanceof Error ? err.message : String(err));
